import React, { useState, useEffect } from 'react';
import { priceSubmissionService, PriceSubmission } from '../services/api';
import { useTranslation } from 'react-i18next';
import { Check, X, ExternalLink, Image as ImageIcon } from 'lucide-react';

const PriceModerationPage: React.FC = () => {
    const { t } = useTranslation();
    const [submissions, setSubmissions] = useState<PriceSubmission[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [processingId, setProcessingId] = useState<number | null>(null);

    useEffect(() => {
        fetchPendingSubmissions();
    }, []);

    const fetchPendingSubmissions = async () => {
        try {
            setLoading(true);
            const response = await priceSubmissionService.getPriceSubmissions({
                status: 'pending',
                limit: 50
            });
            setSubmissions(response.data.data.submissions);
        } catch (err: any) {
            console.error('Error fetching submissions:', err);
            setError(t('priceModeration.error'));
        } finally {
            setLoading(false);
        }
    };

    const handleModerate = async (id: number, status: 'approved' | 'rejected') => {
        try {
            setProcessingId(id);
            await priceSubmissionService.moderateSubmission(id, { status });

            // Remove from list locally
            setSubmissions(prev => prev.filter(s => s.id !== id));

            // Optional: Show success toast
        } catch (err: any) {
            console.error(`Error ${status} submission:`, err);
            alert(`Failed to ${status} submission`);
        } finally {
            setProcessingId(null);
        }
    };

    if (loading) return <div className="p-8 text-center">{t('priceModeration.loading')}</div>;

    return (
        <div className="container mx-auto px-4 py-8">
            <h1 className="text-2xl font-bold mb-6">{t('priceModeration.title')}</h1>

            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    {error}
                </div>
            )}

            {submissions.length === 0 ? (
                <div className="text-center text-gray-500 py-10 bg-white rounded-lg shadow">
                    {t('priceModeration.noSubmissions')}
                </div>
            ) : (
                <div className="space-y-4">
                    {submissions.map(submission => (
                        <div key={submission.id} className="bg-white p-4 rounded-lg shadow border border-gray-200 flex flex-col md:flex-row gap-4">
                            {/* Card Info */}
                            <div className="flex-1">
                                <div className="flex items-start gap-3">
                                    <div className="w-16 h-24 bg-gray-200 rounded overflow-hidden flex-shrink-0">
                                        {submission.card?.imageUrl ? (
                                            <img src={submission.card.imageUrl} alt={submission.card.name} className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">{t('priceModeration.noImg')}</div>
                                        )}
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-lg">{submission.card?.name}</h3>
                                        <p className="text-sm text-gray-600">{submission.card?.cardNumber} - {submission.card?.rarity}</p>
                                        <div className="mt-2 text-xl font-bold text-green-600">
                                            ฿{submission.submittedPrice.toLocaleString()}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Submission Details */}
                            <div className="flex-1 text-sm">
                                <p><strong>{t('priceModeration.source')}:</strong> {submission.source}</p>
                                {submission.sourceUrl && (
                                    <a href={submission.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1 font-medium mt-1 break-all">
                                        {submission.sourceUrl} <ExternalLink size={14} className="flex-shrink-0" />
                                    </a>
                                )}
                                <p className="mt-2"><strong>{t('priceModeration.submittedBy')}:</strong> {submission.submitter?.displayName}</p>
                                <p><strong>{t('priceModeration.date')}:</strong> {new Date(submission.priceDate).toLocaleDateString()}</p>
                                {submission.proofImageUrl && (
                                    <a href={submission.proofImageUrl} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-blue-600 bg-blue-50 px-2 py-1 rounded">
                                        <ImageIcon size={14} /> {t('priceModeration.proofImage')}
                                    </a>
                                )}
                            </div>

                            {/* Actions */}
                            <div className="flex flex-col gap-2 justify-center w-full md:w-32">
                                <button
                                    onClick={() => handleModerate(submission.id, 'approved')}
                                    disabled={processingId === submission.id}
                                    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center justify-center gap-2 disabled:opacity-50"
                                >
                                    <Check size={18} /> {t('priceModeration.approve')}
                                </button>
                                <button
                                    onClick={() => handleModerate(submission.id, 'rejected')}
                                    disabled={processingId === submission.id}
                                    className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 flex items-center justify-center gap-2 disabled:opacity-50"
                                >
                                    <X size={18} /> {t('priceModeration.reject')}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default PriceModerationPage;
